import {
  isEmpty,
  joinWithAnd,
  pluralize,
  type ProgramStatus,
  type ProgramTaskData,
  type ProgramTaskDay,
  type ProgramTaskSequentialWeekday,
  type ProgramTaskWeekday,
} from '@terros/common'
import { WEEKDAYS } from '../competition'

export function stringifySchedule({ schedule }: Pick<ProgramTaskData, 'schedule'>): string {
  switch (schedule.type) {
    case 'day':
      return stringifyDaySchedule(schedule)
    case 'weekday':
      return stringifyWeekdaySchedule(schedule)
    case 'sequentialWeekday':
      return stringifySequentialWeekdaySchedule(schedule)
    case 'none':
      return 'No Schedule'
  }
}

function stringifySequentialWeekdaySchedule({ weekdays }: ProgramTaskSequentialWeekday): string {
  if (isEmpty(weekdays)) return 'No Schedule'
  const days = weekdays.map((day) => WEEKDAYS[day - 1].slice(0, 3))
  return joinWithAnd(days)
}

export function stringifyDaySchedule({ days }: ProgramTaskDay): string {
  if (isEmpty(days)) return 'No Schedule'
  return groupNumbers(days, 'Day')
}

function stringifyWeekdaySchedule({ days }: ProgramTaskWeekday): string {
  if (isEmpty(days)) return 'No Schedule'
  return days
    .sort((a, b) => a.weekday - b.weekday)
    .map((day) => {
      const weeks = stringifyWeeks(day.weeks)
      const dayName = WEEKDAYS[day.weekday - 1].slice(0, 3)
      return `${dayName} (${weeks})`
    })
    .join(', ')
}

export function stringifyWeeks(weeks: number[], capitalize?: boolean): string {
  return groupNumbers(weeks, capitalize ? 'Week' : 'week')
}

function groupNumbers(numbers: number[], label: string): string {
  numbers.sort((a, b) => a - b)
  if (numbers.length === 1) return `${label} ${numbers[0]}`
  const groups = numbers.reduce(
    (acc: { start: number; end: number }[], day) => {
      const last = acc[acc.length - 1]
      if (last && last.end + 1 === day) {
        last.end = day
      } else {
        acc.push({ start: day, end: day })
      }
      return acc
    },
    [] as { start: number; end: number }[]
  )

  const pluralized = pluralize(2, label)

  if (isEmpty(groups)) return ''

  if (groups.length === 1) return `${pluralized} ${stringifyGroup(groups.at(0))}`

  return `${pluralized} ${groups.slice(0, -1).map(stringifyGroup).join(', ')} & ${stringifyGroup(groups.at(-1))}`
}

function stringifyGroup(group?: { start: number; end: number }): string {
  if (!group) return ''
  if (group.start === group.end) return group.start.toString()
  return `${group.start}-${group.end}`
}

export function toReadableProgramStatus(status: ProgramStatus): string {
  switch (status) {
    case 'active':
      return 'Active'
    case 'inactive':
      return 'Inactive'
  }
}
